import { useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api, { formatApiError } from "@/api/client";
import { toast } from "sonner";
import { Camera, Trash2, Upload } from "lucide-react";

// Downscale to 200x200 centered square JPEG, quality 0.85. Returns dataURL string.
async function fileToSquareDataUrl(file, size = 200, quality = 0.85) {
  const img = new Image();
  const buf = await file.arrayBuffer();
  const blobUrl = URL.createObjectURL(new Blob([buf], { type: file.type }));
  try {
    await new Promise((res, rej) => {
      img.onload = res;
      img.onerror = () => rej(new Error("Could not load image"));
      img.src = blobUrl;
    });
    const canvas = document.createElement("canvas");
    canvas.width = size; canvas.height = size;
    const ctx = canvas.getContext("2d");
    // cover-crop
    const minSide = Math.min(img.width, img.height);
    const sx = (img.width - minSide) / 2;
    const sy = (img.height - minSide) / 2;
    ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, size, size);
    return canvas.toDataURL("image/jpeg", quality);
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

export default function AvatarUpload({ open, onOpenChange, user, onUpdated }) {
  const [preview, setPreview] = useState(user?.avatar_url || "");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);

  const onPick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Choose an image file"); return; }
    if (f.size > 8 * 1024 * 1024) { toast.error("Image too large (max 8MB)"); return; }
    try {
      const dataUrl = await fileToSquareDataUrl(f);
      setPreview(dataUrl);
    } catch (err) {
      toast.error("Failed to process image");
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/auth/avatar", { avatar_url: preview });
      toast.success("Photo updated");
      onUpdated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
    } finally { setSaving(false); }
  };

  const clear = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/auth/avatar", { avatar_url: "" });
      setPreview("");
      toast.success("Photo removed");
      onUpdated?.(data);
    } catch (e) {
      toast.error("Remove failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-surface border-white/10 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">Profile photo</DialogTitle>
          <DialogDescription className="text-white/50">
            Upload a square image. It will be cropped &amp; downscaled to 200×200.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col items-center gap-4 py-2">
          <div className="h-32 w-32 rounded-full overflow-hidden lim-gradient grid place-items-center border-2 border-white/10">
            {preview ? (
              <img src={preview} alt="preview" className="h-full w-full object-cover" />
            ) : (
              <span className="font-hero text-5xl uppercase">{user?.name?.[0]}</span>
            )}
          </div>
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
            data-testid="avatar-file-input"
          />
          <div className="flex gap-2">
            <Button
              type="button"
              onClick={onPick}
              data-testid="avatar-pick-btn"
              variant="outline"
              className="border-white/15 bg-black/30 text-white hover:bg-white/5"
            >
              <Camera size={14} className="mr-2" /> Choose photo
            </Button>
            {user?.avatar_url && (
              <Button
                type="button"
                onClick={clear}
                disabled={saving}
                data-testid="avatar-remove-btn"
                variant="outline"
                className="border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200"
              >
                <Trash2 size={14} className="mr-2" /> Remove
              </Button>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            onClick={save}
            disabled={saving || preview === (user?.avatar_url || "")}
            data-testid="avatar-save-btn"
            className="bg-[#E11D2E] hover:bg-[#F03041] rounded-full px-6"
          >
            <Upload size={14} className="mr-2" /> {saving ? "Saving…" : "Save photo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
